import { Chess, type Color, type Move, type PieceSymbol } from 'chess.js';

import {
  deriveAiSeed,
  formatAiSeed,
  nextDeterministicRandom,
  randomUnitInterval
} from './deterministic';
import type { AiMoveDiagnostics, AiMoveProposal, AiMoveRequest, AiTier } from './types';

const PIECE_VALUES: Readonly<Record<PieceSymbol, number>> = {
  p: 100,
  n: 320,
  b: 330,
  r: 500,
  q: 900,
  k: 0
};
const SPROUT_TIER: AiTier = 'sprout';
const PIECE_SCOUT_TIER: AiTier = 'piece-scout';
const SCOUT_TEMPERATURE = 600;
const MATE_SCORE = 100_000;

interface ScoredMove {
  readonly move: Move;
  readonly score: number;
  readonly weight: number;
  readonly hangsQueen: boolean;
}

interface PreparedPosition {
  readonly chess: Chess;
  readonly positionFen: string;
  readonly positionHash: string;
  readonly seed: bigint;
}

/**
 * Proposes a friendly legal move without changing the supplied position or UI state.
 */
export function proposeSproutMove(request: AiMoveRequest): AiMoveProposal {
  const prepared = preparePosition(request, SPROUT_TIER);
  if ('proposal' in prepared) {
    return prepared.proposal;
  }

  const legalMoves = sortedLegalMoves(prepared.chess);
  if (legalMoves.length === 0) {
    return terminalProposal(request, SPROUT_TIER, prepared);
  }

  const scoredMoves = legalMoves.map((move) => scoreSproutMove(move));
  const safeMoves = scoredMoves.filter((candidate) => !candidate.hangsQueen);
  const candidates = safeMoves.length > 0 ? safeMoves : scoredMoves;
  const selected = selectWeightedMove(candidates, prepared.seed);

  return successfulProposal(request, SPROUT_TIER, prepared, selected, {
    legalMoveCount: legalMoves.length,
    candidateCount: candidates.length,
    queenHangCandidatesExcluded: safeMoves.length > 0 ? scoredMoves.length - safeMoves.length : 0,
    temperature: null
  });
}

/**
 * Evaluates each legal move through the opponent's best material reply, then samples
 * from that minimax score with a deliberately high deterministic temperature.
 */
export function proposePieceScoutMove(request: AiMoveRequest): AiMoveProposal {
  const prepared = preparePosition(request, PIECE_SCOUT_TIER);
  if ('proposal' in prepared) {
    return prepared.proposal;
  }

  const legalMoves = sortedLegalMoves(prepared.chess);
  if (legalMoves.length === 0) {
    return terminalProposal(request, PIECE_SCOUT_TIER, prepared);
  }

  const rootColor = prepared.chess.turn();
  const minimaxScores = legalMoves.map((move) => ({
    move,
    score: twoPlyMaterialMinimax(move, rootColor)
  }));
  const bestScore = Math.max(...minimaxScores.map((candidate) => candidate.score));
  const scoredMoves = minimaxScores.map(({ move, score }) => ({
    move,
    score,
    weight: temperatureWeight(score, bestScore),
    hangsQueen: false
  }));
  const selected = selectWeightedMove(scoredMoves, prepared.seed);

  return successfulProposal(request, PIECE_SCOUT_TIER, prepared, selected, {
    legalMoveCount: legalMoves.length,
    candidateCount: scoredMoves.length,
    queenHangCandidatesExcluded: 0,
    temperature: SCOUT_TEMPERATURE
  });
}

function preparePosition(
  request: AiMoveRequest,
  tier: AiTier
): PreparedPosition | { readonly proposal: AiMoveProposal } {
  try {
    const chess = new Chess(request.fen);
    const positionFen = chess.fen();
    const positionHash = chess.hash();
    const seed = deriveAiSeed({
      gameSeed: request.gameSeed,
      tierVersion: request.tierVersion,
      engineBuild: request.engineBuild,
      positionFen,
      positionHash,
      ply: request.ply
    });

    return { chess, positionFen, positionHash, seed };
  } catch (error) {
    return {
      proposal: {
        status: 'invalid-fen',
        uci: null,
        diagnostics: failedDiagnostics(request, tier, 'invalid-fen', error)
      }
    };
  }
}

function sortedLegalMoves(chess: Chess): Move[] {
  return [...chess.moves({ verbose: true })].sort((left, right) =>
    left.lan.localeCompare(right.lan)
  );
}

function scoreSproutMove(move: Move): ScoredMove {
  const hangsQueen = move.piece === 'q' && queenCanBeCapturedImmediately(move);
  let score = 1;

  if (isDevelopingMove(move)) {
    score += 3;
  }
  if (move.san.endsWith('+') || move.san.endsWith('#')) {
    score += 4;
  }
  if (move.isCapture() && move.captured) {
    score += 2 + PIECE_VALUES[move.captured] / 100;
  }
  if (move.isPromotion()) {
    score += 8;
  }

  return {
    move,
    score,
    weight: score,
    hangsQueen
  };
}

function isDevelopingMove(move: Move): boolean {
  const homeRank = move.color === 'w' ? '1' : '8';
  const isMinorPieceDevelopment =
    (move.piece === 'n' || move.piece === 'b') && move.from.endsWith(homeRank);
  const isCentralPawnAdvance =
    move.piece === 'p' &&
    move.from.endsWith(move.color === 'w' ? '2' : '7') &&
    (move.to.startsWith('d') || move.to.startsWith('e'));

  return isMinorPieceDevelopment || isCentralPawnAdvance;
}

function queenCanBeCapturedImmediately(move: Move): boolean {
  const afterMove = new Chess(move.after);
  return afterMove.moves({ verbose: true }).some((reply) =>
    reply.isCapture() && reply.to === move.to
  );
}

function twoPlyMaterialMinimax(move: Move, rootColor: Color): number {
  const afterMove = new Chess(move.after);
  const replies = sortedLegalMoves(afterMove);

  if (replies.length === 0) {
    return evaluateMaterial(afterMove, rootColor);
  }

  return Math.min(
    ...replies.map((reply) => evaluateMaterial(new Chess(reply.after), rootColor))
  );
}

function evaluateMaterial(chess: Chess, rootColor: Color): number {
  if (chess.isCheckmate()) {
    return chess.turn() === rootColor ? -MATE_SCORE : MATE_SCORE;
  }
  if (chess.isDraw()) {
    return 0;
  }

  return chess.board().flat().reduce((score, square) => {
    if (!square) {
      return score;
    }

    const multiplier = square.color === rootColor ? 1 : -1;
    return score + multiplier * PIECE_VALUES[square.type];
  }, 0);
}

function temperatureWeight(score: number, bestScore: number): number {
  return Math.exp((score - bestScore) / SCOUT_TEMPERATURE);
}

function selectWeightedMove(candidates: readonly ScoredMove[], seed: bigint): ScoredMove {
  const totalWeight = candidates.reduce((total, candidate) => total + candidate.weight, 0);
  const draw = randomUnitInterval(nextDeterministicRandom(seed).value) * totalWeight;
  let cumulativeWeight = 0;

  for (const candidate of candidates) {
    cumulativeWeight += candidate.weight;
    if (draw < cumulativeWeight) {
      return candidate;
    }
  }

  return candidates[candidates.length - 1];
}

function successfulProposal(
  request: AiMoveRequest,
  tier: AiTier,
  prepared: PreparedPosition,
  selected: ScoredMove,
  details: Pick<
    AiMoveDiagnostics,
    'legalMoveCount' | 'candidateCount' | 'queenHangCandidatesExcluded' | 'temperature'
  >
): AiMoveProposal {
  return {
    status: 'ok',
    uci: selected.move.lan,
    diagnostics: {
      tier,
      tierVersion: request.tierVersion,
      engineBuild: request.engineBuild,
      ply: request.ply,
      positionFen: prepared.positionFen,
      positionHash: prepared.positionHash,
      seed: formatAiSeed(prepared.seed),
      legalMoveCount: details.legalMoveCount,
      candidateCount: details.candidateCount,
      selectedScore: selected.score,
      selectedWeight: selected.weight,
      temperature: details.temperature,
      queenHangCandidatesExcluded: details.queenHangCandidatesExcluded,
      failureReason: null
    }
  };
}

function terminalProposal(
  request: AiMoveRequest,
  tier: AiTier,
  prepared: PreparedPosition
): AiMoveProposal {
  return {
    status: 'terminal',
    uci: null,
    diagnostics: {
      tier,
      tierVersion: request.tierVersion,
      engineBuild: request.engineBuild,
      ply: request.ply,
      positionFen: prepared.positionFen,
      positionHash: prepared.positionHash,
      seed: formatAiSeed(prepared.seed),
      legalMoveCount: 0,
      candidateCount: 0,
      selectedScore: null,
      selectedWeight: null,
      temperature: tier === PIECE_SCOUT_TIER ? SCOUT_TEMPERATURE : null,
      queenHangCandidatesExcluded: 0,
      failureReason: terminalReason(prepared.chess)
    }
  };
}

function failedDiagnostics(
  request: AiMoveRequest,
  tier: AiTier,
  failureReason: string,
  error: unknown
): AiMoveDiagnostics {
  return {
    tier,
    tierVersion: request.tierVersion,
    engineBuild: request.engineBuild,
    ply: request.ply,
    positionFen: request.fen,
    positionHash: null,
    seed: null,
    legalMoveCount: 0,
    candidateCount: 0,
    selectedScore: null,
    selectedWeight: null,
    temperature: tier === PIECE_SCOUT_TIER ? SCOUT_TEMPERATURE : null,
    queenHangCandidatesExcluded: 0,
    failureReason: error instanceof Error ? `${failureReason}: ${error.message}` : failureReason
  };
}

function terminalReason(chess: Chess): string {
  if (chess.isCheckmate()) {
    return 'checkmate';
  }
  if (chess.isStalemate()) {
    return 'stalemate';
  }
  if (chess.isDraw()) {
    return 'draw';
  }
  return 'no-legal-moves';
}
